const makeMlsLogGate = () => {
  const original = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug ? console.debug.bind(console) : console.log.bind(console),
  };

  let hasLogged = false;
  const BENIGN_WARN_PATTERNS = [
    'joingroup failed with ratchet tree; retrying without tree',
    'worker key package mismatch',
    'refreshing cached key package',
    'skipping welcome with mismatched ref',
    'welcome key package ref mismatch; attempting recovery before join',
    'group not ready; pause resend and self-heal',
    'group not ready — gating pending welcome/commit',
  ];
  const ALWAYS_REPORT_PATTERNS = [
    'applyserversnapshot failed',
    '[mls worker] joingroup failed',
    '[mls] joingroup failed',
    'missing key package',
    'forcejoinbundle joingroup failed',
    'pending converge failed during snapshot apply',
  ];

  const toSerializable = (value) => {
    if (value == null) return value;
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }
    if (typeof value === 'object') {
      try {
        const plain = JSON.parse(JSON.stringify(value));
        if (
          plain &&
          typeof plain === 'object' &&
          Object.keys(plain).length === 0 &&
          Object.getOwnPropertyNames(value).length > 0
        ) {
          const enriched = {};
          for (const key of Object.getOwnPropertyNames(value)) {
            try {
              enriched[key] = value[key];
            } catch {}
          }
          return enriched;
        }
        return plain;
      } catch {
        try {
          const enriched = {};
          for (const key of Object.getOwnPropertyNames(value)) {
            try {
              enriched[key] = value[key];
            } catch {}
          }
          if (Object.keys(enriched).length > 0) return enriched;
        } catch {}
        try {
          return { value: String(value) };
        } catch {
          return { value: '[unserializable]' };
        }
      }
    }
    return value;
  };

  const extractMessage = (args) => {
    if (!args || !args.length) return '';
    for (const arg of args) {
      if (typeof arg === 'string') return arg;
      if (arg && typeof arg.message === 'string') return arg.message;
    }
    return '';
  };

  const isMlsMessage = (args) => {
    if (!args || !args.length) return false;
    for (const arg of args) {
      if (typeof arg === 'string' && arg.includes('[MLS')) return true;
      if (arg && typeof arg.message === 'string' && arg.message.includes('[MLS')) return true;
    }
    return false;
  };

  const shouldReportAsError = (level, args) => {
    const lower = String(extractMessage(args) || '').toLowerCase();
    const isBenignWarn =
      level === 'warn' &&
      BENIGN_WARN_PATTERNS.some((pattern) => lower.includes(pattern));
    if (isBenignWarn) return false;
    if (level === 'error' || level === 'warn') return true;
    return args.some((arg) => arg instanceof Error);
  };

  const shouldAlwaysReport = (args) => {
    const lower = String(extractMessage(args) || '').toLowerCase();
    return ALWAYS_REPORT_PATTERNS.some((pattern) => lower.includes(pattern));
  };

  const reportOnce = (level, args) => {
    const alwaysReport = shouldAlwaysReport(args);
    if (!alwaysReport && hasLogged) return;
    if (!alwaysReport) {
      hasLogged = true;
    }
    const firstString = extractMessage(args) || null;
    const errorObj = args.find((arg) => arg instanceof Error) || null;
    const payload = {
      level,
      message: firstString,
      error: errorObj ? { name: errorObj.name, message: errorObj.message, stack: errorObj.stack } : null,
      data: args.map((arg) => toSerializable(arg)),
    };
    original.error('[MLS] error', payload);
    if (alwaysReport) {
      try {
        original.error(`[MLS] error detail ${JSON.stringify(payload)}`);
      } catch {}
    }
  };

  const wrap = (level) => (...args) => {
    if (isMlsMessage(args)) {
      if (shouldReportAsError(level, args)) {
        reportOnce(level, args);
      }
      return;
    }
    return original[level](...args);
  };

  console.log = wrap('log');
  console.info = wrap('info');
  console.warn = wrap('warn');
  console.error = wrap('error');
  if (console.debug) {
    console.debug = wrap('debug');
  }
};

makeMlsLogGate();
